import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  addDoc,
  serverTimestamp } from
"firebase/firestore";
import { getIdToken } from "firebase/auth";
import { db, auth } from "./firebase";
import useUserData from "../hooks/useUserData";
import { useNavigate } from "react-router-dom";
import { getCachedRoster, bumpRosterVersion, clearCachedRoster } from "./RosterCache";
import { SiteStateContext } from "./SiteStateContext";
import { getDungeonIntelMap } from "./dgIntel";
import { applyTeamTheme } from "./applyTeamTheme";
import { DEFAULT_TRADE_B_ITEMS } from "../constants/tradeBayDefaults";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button } from
"@mui/material";

const clampTradeBayQty = (qty) => {
  const parsed = Number.parseInt(qty, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return 1;
  }
  return parsed;
};

const normalizeTradeBayGear = (items) => {
  if (!Array.isArray(items)) return [];
  const merged = new Map();
  items.forEach((item) => {
    const rawName = typeof item === "string" ? item : item?.name;
    const name = String(rawName || "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    const qtySource = typeof item === "string" ? 1 : item?.qty;
    const qty = clampTradeBayQty(qtySource);
    if (merged.has(key)) {
      merged.get(key).qty += qty;
    } else {
      merged.set(key, { name, qty });
    }
  });
  return Array.from(merged.values());
};

const defaultTradeBayGear = () =>
DEFAULT_TRADE_B_ITEMS.map((name) => ({ name, qty: 1 }));

const cloneTradeBayGear = (items) =>
items.map((item) => ({ name: item.name, qty: item.qty }));

const TeamLeaderPanel = () => {
  const { userData, loading } = useUserData();
  const navigate = useNavigate();
  const { impersonateTeamId, impersonateTeamName, isImpersonating } =
  useContext(SiteStateContext);
  const isAdmin = Boolean(userData?.adminUser);
  const isAdminImpersonating = isImpersonating && isAdmin;
  const effectiveTeamId =
  isAdminImpersonating ? impersonateTeamId : userData?.teamId;
  const effectiveTeamName =
  isAdminImpersonating ?
  impersonateTeamName || userData?.teamName || "" :
  userData?.teamName || "";
  const isLeader = userData?.role === "leader" || isAdminImpersonating;


  const TABS = useMemo(
    () => ({
      MEMBERS: "members",
      ROSTER: "roster",
      SITE_SETTINGS: "site-settings"
    }),
    []
  );

  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem("leaderPanelActiveTab");
    return saved || TABS.SITE_SETTINGS;
  });


  const [team, setTeam] = useState(null);
  const [mainColor, setMainColor] = useState("#330055");
  const [hoverColor, setHoverColor] = useState("#2a2a2a");
  const [textColor, setTextColor] = useState("#ffffff");
  const [specialColor, setSpecialColor] = useState("#ff691e");
  const [leaderNameInput, setLeaderNameInput] = useState("");


  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);


  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(true);


  const [scanCounts, setScanCounts] = useState({ galaxies: null, planets: null });
  const [proddyLogCount, setProddyLogCount] = useState(null);
  const [dgScanCount, setDgScanCount] = useState(null);


  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoTitle, setInfoTitle] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [infoAutoClose, setInfoAutoClose] = useState(true);
  const [infoAutoCloseMs, setInfoAutoCloseMs] = useState(1000);
  const [scanManageOpen, setScanManageOpen] = useState(false);
  const [scanManageLoading, setScanManageLoading] = useState(false);
  const [scanManageData, setScanManageData] = useState(null);
  const [customTradeBayOpen, setCustomTradeBayOpen] = useState(false);
  const [customTradeBayLoading, setCustomTradeBayLoading] = useState(false);
  const [customTradeBaySaving, setCustomTradeBaySaving] = useState(false);
  const [customTradeBayItems, setCustomTradeBayItems] = useState([]);
  const [customTradeBayLoadedItems, setCustomTradeBayLoadedItems] = useState([]);
  const [customTradeBaySuggestionInput, setCustomTradeBaySuggestionInput] = useState("");
  const [customTradeBayNewItemInput, setCustomTradeBayNewItemInput] = useState("");
  const [rosterEditOpen, setRosterEditOpen] = useState(false);
  const [rosterEditEntry, setRosterEditEntry] = useState(null);
  const [rosterEditMain, setRosterEditMain] = useState("");
  const [rosterEditAlts, setRosterEditAlts] = useState("");
  const [rosterEditSaving, setRosterEditSaving] = useState(false);


  const buttonStyle = {
    padding: "6px 12px",
    backgroundColor: "var(--theme-color)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.9rem"
  };

  const panelCardStyle = {
    backgroundColor: "#2a2a2a",
    border: "1px solid #444",
    borderRadius: "8px",
    padding: "1rem",
    boxShadow: "0 4px 12px rgba(0,0,0,0.25)"
  };

  const dangerButtonStyle = {
    padding: "6px 12px",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.9rem",
    backgroundColor: "var(--theme-color)",
    marginLeft: "0.5rem"
  };

  const colorInputStyle = {
    width: "56px",
    height: "32px",
    padding: "2px 4px",
    borderRadius: "6px",
    border: "1px solid #666",
    backgroundColor: "#1a1a1a",
    cursor: "pointer"
  };

  const canManageCustomTradeBay = isLeader && Boolean(effectiveTeamId);
  const hasSavedCustomTradeBay = Array.isArray(team?.customTradeBayGear);
  const savedCustomTradeBayCount = normalizeTradeBayGear(team?.customTradeBayGear).length;
  const customTradeBayStatus = hasSavedCustomTradeBay ?
  `${savedCustomTradeBayCount} saved item${savedCustomTradeBayCount === 1 ? "" : "s"}` :
  "Using defaults";
  const customTradeBaySuggestions = useMemo(() => {
    const seen = new Map();
    [...DEFAULT_TRADE_B_ITEMS, ...customTradeBayItems.map((item) => item.name)].forEach((rawName) => {
      const name = String(rawName || "").trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (!seen.has(key)) {
        seen.set(key, name);
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [customTradeBayItems]);

  const openConfirm = (title, message, onConfirm) => {
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmAction(() => onConfirm || null);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmAction(null);
  };

  const handleConfirm = async () => {
    if (confirmAction) {
      await confirmAction();
    }
    closeConfirm();
  };

  const openInfo = (title, message, autoClose = true, autoCloseMs = 1000) => {
    setInfoTitle(title);
    setInfoMessage(message);
    setInfoAutoClose(autoClose);
    setInfoAutoCloseMs(autoCloseMs);
    setInfoOpen(true);
  };

  const closeInfo = () => {
    setInfoOpen(false);
    if (scanManageOpen) {
      refreshScanManage();
    }
  };

  const refreshScanManage = () => {
    if (!effectiveTeamId) return;
    setScanManageLoading(true);
    fetch(
      `https://imp.slypandainteractive.com/team_scan_list.php?team_id=${encodeURIComponent(
        effectiveTeamId
      )}`
    ).
    then((res) => res.json()).
    then((data) => setScanManageData(data)).
    catch((err) => {
      console.error("Error loading scan list:", err);
      setScanManageData(null);
    }).
    finally(() => setScanManageLoading(false));
  };

  useEffect(() => {
    if (!infoOpen || !infoAutoClose) return undefined;
    const timer = setTimeout(() => {
      closeInfo();
    }, infoAutoCloseMs);
    return () => clearTimeout(timer);
  }, [infoOpen, infoAutoClose, infoAutoCloseMs]);

  const loadScanCounts = () => {
    if (!effectiveTeamId) return;
    fetch(
      `https://imp.slypandainteractive.com/team_scan_counts.php?team_id=${encodeURIComponent(
        effectiveTeamId
      )}`
    ).
    then((res) => res.json()).
    then((data) => {
      const galaxies = Number(data?.galaxies);
      const planets = Number(data?.planets);
      setScanCounts({
        galaxies: Number.isFinite(galaxies) ? galaxies : null,
        planets: Number.isFinite(planets) ? planets : null
      });
    }).
    catch((err) => {
      console.error("Error loading scan count:", err);
      setScanCounts({ galaxies: null, planets: null });
    });
  };

  const loadProddyLogCount = () => {
    if (!effectiveTeamId) return;
    fetch(
      `https://imp.slypandainteractive.com/proddy_counts.php?team_id=${encodeURIComponent(
        effectiveTeamId
      )}`
    ).
    then((res) => res.json()).
    then((data) => {
      const count = Number(data?.log_count);
      setProddyLogCount(Number.isFinite(count) ? count : null);
    }).
    catch((err) => {
      console.error("Error loading proddy log count:", err);
      setProddyLogCount(null);
    });
  };


  const openScanManage = () => {
    if (!effectiveTeamId) return;
    setScanManageOpen(true);
    refreshScanManage();
  };

  const closeScanManage = () => {
    setScanManageOpen(false);
  };

  const closeCustomTradeBay = () => {
    setCustomTradeBayItems(cloneTradeBayGear(customTradeBayLoadedItems));
    setCustomTradeBaySuggestionInput("");
    setCustomTradeBayNewItemInput("");
    setCustomTradeBayOpen(false);
  };

  const openCustomTradeBay = async () => {
    if (!canManageCustomTradeBay) return;
    setCustomTradeBayOpen(true);
    setCustomTradeBayLoading(true);
    setCustomTradeBaySuggestionInput("");
    setCustomTradeBayNewItemInput("");
    try {
      const teamRef = doc(db, "teams", effectiveTeamId);
      const snap = await getDoc(teamRef);
      const teamData = snap.exists() ? snap.data() : {};
      const savedGear = normalizeTradeBayGear(teamData?.customTradeBayGear);
      const hasSavedField = Array.isArray(teamData?.customTradeBayGear);
      const nextItems = hasSavedField ? savedGear : defaultTradeBayGear();
      const nextItemsClone = cloneTradeBayGear(nextItems);
      setCustomTradeBayItems(nextItemsClone);
      setCustomTradeBayLoadedItems(nextItemsClone);
      if (snap.exists()) {
        setTeam(teamData);
      }
    } catch (err) {
      console.error("Failed to load custom trade bay:", err);
      const fallback = defaultTradeBayGear();
      const fallbackClone = cloneTradeBayGear(fallback);
      setCustomTradeBayItems(fallbackClone);
      setCustomTradeBayLoadedItems(fallbackClone);
      openInfo("Load Failed", "Could not load custom trade bay. Using defaults.", true, 2000);
    } finally {
      setCustomTradeBayLoading(false);
    }
  };

  const handleCustomTradeBayQtyChange = (index, nextQty) => {
    const qty = clampTradeBayQty(nextQty);
    setCustomTradeBayItems((prev) =>
    prev.map((item, itemIndex) => itemIndex === index ? { ...item, qty } : item)
    );
  };

  const handleCustomTradeBayRemove = (index) => {
    setCustomTradeBayItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleCustomTradeBayAdd = () => {
    const incomingName = (customTradeBayNewItemInput.trim() || customTradeBaySuggestionInput.trim()).trim();
    if (!incomingName) return;
    const lowerIncoming = incomingName.toLowerCase();
    setCustomTradeBayItems((prev) => {
      const existingIndex = prev.findIndex((item) => item.name.toLowerCase() === lowerIncoming);
      if (existingIndex >= 0) {
        return prev.map((item, itemIndex) =>
        itemIndex === existingIndex ? { ...item, qty: item.qty + 1 } : item
        );
      }
      return [...prev, { name: incomingName, qty: 1 }];
    });
    setCustomTradeBaySuggestionInput("");
    setCustomTradeBayNewItemInput("");
  };

  const handleCustomTradeBaySave = async () => {
    if (!canManageCustomTradeBay) return;
    setCustomTradeBaySaving(true);
    try {
      const normalizedItems = normalizeTradeBayGear(customTradeBayItems);
      const teamRef = doc(db, "teams", effectiveTeamId);
      const updatedByUid = auth.currentUser?.uid || userData?.uid || "";
      await updateDoc(teamRef, {
        customTradeBayGear: normalizedItems,
        customTradeBayUpdatedAt: serverTimestamp(),
        customTradeBayUpdatedByUid: updatedByUid
      });
      setTeam((prev) => ({
        ...(prev || {}),
        customTradeBayGear: normalizedItems,
        customTradeBayUpdatedByUid: updatedByUid
      }));
      const normalizedClone = cloneTradeBayGear(normalizedItems);
      setCustomTradeBayItems(normalizedClone);
      setCustomTradeBayLoadedItems(normalizedClone);
      setCustomTradeBayOpen(false);
      openInfo("Saved", "Custom Trade Bay updated.", true, 1500);
    } catch (err) {
      console.error("Failed to save custom trade bay:", err);
      openInfo("Save Failed", "Could not save custom trade bay.", true, 2000);
    } finally {
      setCustomTradeBaySaving(false);
    }
  };

  const handleDeleteGalaxyScans = async (galaxy) => {
    try {
      const res = await fetch("https://imp.slypandainteractive.com/delete_entry.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          galaxy,
          teamId: String(effectiveTeamId)
        })
      });
      const text = await res.text();
      openInfo("Result", text || "Delete completed.");
      openScanManage();
      loadScanCounts();
    } catch (err) {
      console.error("Error deleting galaxy scans:", err);
      openInfo("Delete Failed", "Failed to delete galaxy scans.");
    }
  };

  const handleDeleteBodyScan = async (bodyId, bodyName) => {
    try {
      const res = await fetch("https://imp.slypandainteractive.com/delete_scan_body.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          body_id: String(bodyId),
          team_id: String(effectiveTeamId)
        })
      });
      const text = await res.text();
      openInfo("Delete Result", text || `Deleted ${bodyName}.`);
      openScanManage();
      loadScanCounts();
    } catch (err) {
      console.error("Error deleting scan entry:", err);
      openInfo("Delete Failed", "Failed to delete scan entry.");
    }
  };

  const handleDeleteProddyTest = async () => {
    try {
      if (!auth.currentUser) {
        openInfo("Delete Failed", "You must be signed in.", true, 3000);
        return;
      }

      openInfo("Deleting...", "Clearing proddy data. This can take a moment.", false);
      const token = await getIdToken(auth.currentUser, true);
      const res = await fetch("https://imp.slypandainteractive.com/delete_proddy.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          idToken: token,
          mode: "full",
          scope: "both"
        })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data?.error || "Failed to delete proddy entries.";
        openInfo("Delete Failed", msg, true, 3000);
        return;
      }

      openInfo(
        "Delete Result",
        `Deleted ${data.deleted_log_count ?? 0} log entries and ${
        data.deleted_snapshot_count ?? 0} snapshot entries for team ${
        userData?.teamName || data.team_id || ""}.`,
        true,
        3000
      );
      loadProddyLogCount();
    } catch (err) {
      console.error("Error deleting proddy entries:", err);
      openInfo("Delete Failed", "Failed to delete proddy entries.", true, 3000);
    }
  };

  const handleDeleteDgScanBatch = async () => {
    try {
      if (!auth.currentUser) {
        openInfo("Delete Failed", "You must be signed in.", true, 3000);
        return;
      }
      if (!effectiveTeamId) {
        openInfo("Delete Failed", "No team selected.", true, 3000);
        return;
      }

      openInfo("Deleting DG Scans...", "Deleting... please do not close this window.", false);
      const readRes = await fetch(
        `https://imp.slypandainteractive.com/dg_scan_read.php?team_id=${encodeURIComponent(
          effectiveTeamId
        )}`
      );
      if (!readRes.ok) {
        openInfo("Delete Failed", "Failed to load DG entries.", true, 3000);
        return;
      }

      const readData = await readRes.json().catch(() => ({}));
      const entries = Array.isArray(readData) ? readData : readData?.dungeons || [];
      if (!entries.length) {
        openInfo("Delete Result", "No DG entries found for this team.", true, 2500);
        return;
      }
      const targets = entries.
      map((entry) => ({
        galaxy: String(entry?.dungeonGalaxyName || "").trim(),
        dgId: String(entry?.dungeonId || "").trim()
      })).
      filter((entry) => entry.galaxy && entry.dgId);

      if (!targets.length) {
        openInfo("Delete Result", "No valid DG entries found for this team.", true, 2500);
        return;
      }

      const token = await getIdToken(auth.currentUser, true);
      let deletedCount = 0;
      for (let i = 0; i < targets.length; i += 1) {
        const target = targets[i];
        const deleteRes = await fetch("https://imp.slypandainteractive.com/delete_dg_scan.php", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            idToken: token,
            teamId: effectiveTeamId,
            galaxy: target.galaxy,
            dgId: target.dgId
          })
        });
        const payload = await deleteRes.json().catch(() => ({}));
        if (!deleteRes.ok || !payload?.ok) {
          const msg = payload?.error?.message || "Failed to delete DG entry.";
          openInfo("Delete Failed", msg, true, 3000);
          return;
        }
        deletedCount += 1;
      }

      const updatedMap = await getDungeonIntelMap(effectiveTeamId, { force: true });
      setDgScanCount(updatedMap.size);
      openInfo(
        "Delete Result",
        `Deleted ${deletedCount} DG entr${deletedCount === 1 ? "y" : "ies"}.`,
        true,
        3000
      );
    } catch (err) {
      console.error("Error deleting DG entry:", err);
      openInfo("Delete Failed", "Failed to delete DG entry.", true, 3000);
    }
  };

  const handleDownloadProddySnapshot = async () => {
    try {
      if (!auth.currentUser) {
        openInfo("Download Failed", "You must be signed in.", true, 3000);
        return;
      }

      openInfo("Preparing Download...", "Generating your proddy snapshot export.", false);
      const token = await getIdToken(auth.currentUser, true);
      const res = await fetch("https://imp.slypandainteractive.com/export_proddy.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          idToken: token,
          scope: "snapshot"
        })
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        openInfo("Download Failed", errText || "Failed to export proddy data.", true, 3000);
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      const teamName = (effectiveTeamName || "team").replace(/[^A-Za-z0-9_-]+/g, "_");
      link.href = url;
      link.download = `${teamName}_proddy_snapshot_${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      openInfo("Download Ready", "Your proddy snapshot download has started.", true, 3000);
    } catch (err) {
      console.error("Error exporting proddy data:", err);
      openInfo("Download Failed", "Failed to export proddy data.", true, 3000);
    }
  };


  useEffect(() => {
    if (!loading) {
      if (!userData) {
        navigate("/starsonata");
      } else if (!isAdminImpersonating && effectiveTeamId === "unassigned") {
        navigate("/join");
      } else if (!isAdminImpersonating && !userData.verified) {
        navigate("/starsonata");
      } else if (!isLeader) {
        navigate("/starsonata/team");
      }
    }
  }, [userData, loading, navigate, isAdminImpersonating, effectiveTeamId, isLeader]);

  useEffect(() => {
    localStorage.setItem("leaderPanelActiveTab", activeTab);
  }, [activeTab]);


  function secureRandomStringWithDashes(totalLength = 20, groupSize = 5) {
    const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZ" +
    "abcdefghijklmnopqrstuvwxyz" +
    "0123456789";

    const randomValues = new Uint8Array(totalLength);
    window.crypto.getRandomValues(randomValues);

    const raw = Array.from(randomValues, (v) => chars[v % chars.length]).join("");

    const parts = raw.match(new RegExp(`.{1,${groupSize}}`, "g"));
    return parts.join("-");
  }

  async function syncUserAuth({ username, uniqueid, teamName, teamId }) {
    if (!username || !uniqueid || !teamId) {
      throw new Error("username, uniqueid and teamId are required");
    }

    const res = await fetch("https://imp.slypandainteractive.com/updatelog.php", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        username,
        uniqueid,
        teamName,
        teamId
      }).toString()
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => res.statusText);
      throw new Error(`Sync failed (${res.status}): ${txt}`);
    }

    return res.text();
  }


  const handleApplyColors = async () => {
    const teamRef = doc(db, "teams", effectiveTeamId);
    await updateDoc(teamRef, {
      themeColor: mainColor,
      hoverColor: hoverColor,
      textColor: textColor,
      specialColor: specialColor
    });

    applyTeamTheme({
      themeColor: mainColor,
      hoverColor,
      textColor,
      specialColor
    });

    alert("Theme updated!");
  };


  useEffect(() => {
    if (effectiveTeamId) {
      const teamRef = doc(db, "teams", effectiveTeamId);
      getDoc(teamRef).then((snap) => {
        if (snap.exists()) {
          const teamData = snap.data();
          setTeam(teamData);
          setMainColor(teamData.themeColor || "#330055");
          setHoverColor(teamData.hoverColor || "#2a2a2a");
          setTextColor(teamData.textColor || "#ffffff");
          setSpecialColor(teamData.specialColor || "#ff691e");
          setLeaderNameInput(teamData.leaderName || userData?.username || "");
        }
      });
    }
  }, [effectiveTeamId, userData?.username]);

  const handleLeaderNameSave = async () => {
    if (!effectiveTeamId) return;
    const nextName = leaderNameInput.trim();
    if (!nextName) {
      openInfo("Validation", "Leader name cannot be empty.", true, 1500);
      return;
    }
    try {
      await updateDoc(doc(db, "teams", effectiveTeamId), {
        leaderName: nextName
      });
      setTeam((prev) => ({ ...(prev || {}), leaderName: nextName }));
      openInfo("Saved", "Team leader name updated.", true, 1500);
    } catch (err) {
      console.error("Failed to update leader name:", err);
      openInfo("Save Failed", "Could not update leader name.", true, 2000);
    }
  };


  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!effectiveTeamId || !isLeader) return;
      setLoadingMembers(true);
      try {
        const q = query(collection(db, "users"), where("teamId", "==", effectiveTeamId));
        const snapshot = await getDocs(q);
        const users = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

        setMembers(users);
      } catch (err) {

        setMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    };

    if (isLeader) {
      fetchTeamMembers();
    } else {
      setLoadingMembers(false);
    }
  }, [effectiveTeamId, isLeader]);


  const handleTokenGeneration = async (userId, username) => {
    if (!window.confirm("Generate New Token? Any old tokens for this user will be deleted."))
    return;

    //const prefix = (userData?.teamName || "TEAM").slice(0, 3);
    const prefix = (userData?.teamName || "TEAM").replaceAll(" ", "-");
    
    
    const newToken = prefix + "-" + secureRandomStringWithDashes();
    const testprefix = newToken.toLowerCase();
    //console.log("Generated Token: " + prefix + " " + testprefix);
    
    await updateDoc(doc(db, "users", userId), { userToken: newToken });

    try {
      const msg = await syncUserAuth({
        username,
        uniqueid: newToken,
        teamName: userData.teamName,
        teamId: effectiveTeamId
      });
      console.log(msg);
    } catch (err) {
      console.error("Sync failed:", err);
      alert("Failed to sync auth table: " + err.message);
    }

    setMembers((prev) =>
    prev.map((m) => m.id === userId ? { ...m, userToken: newToken } : m)
    );
  };

  const handleApprove = async (userId) => {
    await updateDoc(doc(db, "users", userId), { verified: true });
    setMembers((prev) => prev.map((m) => m.id === userId ? { ...m, verified: true } : m));
  };

  const handleRemove = async (userId) => {
    await updateDoc(doc(db, "users", userId), {
      teamId: "unassigned",
      teamName: "Unassigned",
      verified: false
    });
    setMembers((prev) => prev.filter((m) => m.id !== userId));
  };


  useEffect(() => {
    if (effectiveTeamId) {
      setLoadingRoster(true);
      getCachedRoster(effectiveTeamId).
      then((data) => setRoster(data)).
      finally(() => setLoadingRoster(false));
    } else {
      setLoadingRoster(false);
    }
  }, [effectiveTeamId]);


  useEffect(() => {
    if (!userData?.verified || !effectiveTeamId) return;
    loadScanCounts();
  }, [effectiveTeamId, userData?.verified]);

  useEffect(() => {
    if (!userData?.verified || !effectiveTeamId) return;
    let cancelled = false;
    getDungeonIntelMap(effectiveTeamId).
    then((map) => {
      if (!cancelled) {
        setDgScanCount(map.size);
      }
    }).
    catch(() => {
      if (!cancelled) {
        setDgScanCount(null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveTeamId, userData?.verified]);

  useEffect(() => {
    if (!userData?.verified || !effectiveTeamId) return;
    loadProddyLogCount();
  }, [effectiveTeamId, userData?.verified]);

  const handleDeleteRosterEntry = async (entryId) => {
    const ref = doc(db, "teams", effectiveTeamId, "Roster", entryId);
    await deleteDoc(ref);
    setRoster((prev) => prev.filter((e) => e.id !== entryId));
    await bumpRosterVersion(effectiveTeamId);
    clearCachedRoster();
  };


  const RosterForm = () => {
    const [mainCharacter, setMainCharacter] = useState("");
    const [altCharacters, setAltCharacters] = useState("");

    const handleSubmit = async (e) => {
      e.preventDefault();
      const ref = collection(db, "teams", effectiveTeamId, "Roster");

      await addDoc(ref, {
        mainCharacterName: mainCharacter.trim(),
        altCharacters: altCharacters.
        split(",").
        map((s) => s.trim()).
        filter(Boolean)
      });

      const snapshot = await getDocs(ref);
      setRoster(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));

      setMainCharacter("");
      setAltCharacters("");

      await bumpRosterVersion(effectiveTeamId);
      clearCachedRoster();
    };

    return (
      <form
        onSubmit={handleSubmit}
        style={{
          marginTop: "1rem",
          border: "1px solid #ccc",
          padding: "1rem",
          borderRadius: "8px"
        }}>
        
        <h3>Add To Roster</h3>
        <div style={{ marginBottom: "0.5rem" }}>
          <label>Main Character: </label>
          <input
            type="text"
            value={mainCharacter}
            onChange={(e) => setMainCharacter(e.target.value)}
            required />
          
        </div>

        <div>
          <label>Alternative Characters (comma separated): </label>
          <input
            type="text"
            value={altCharacters}
            onChange={(e) => setAltCharacters(e.target.value)}
            required />
          
        </div>

        <button
          type="submit"
          style={{
            marginTop: "0.75rem",
            padding: "10px 20px",
            backgroundColor: "var(--theme-color)",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "1rem"
          }}>
          
          Add Roster Entry
        </button>
      </form>);

  };


  const openRosterEdit = (entry) => {
    setRosterEditEntry(entry);
    setRosterEditMain(entry?.mainCharacterName || "");
    setRosterEditAlts(Array.isArray(entry?.altCharacters) ? entry.altCharacters.join("\n") : "");
    setRosterEditOpen(true);
  };

  const closeRosterEdit = () => {
    if (rosterEditSaving) return;
    setRosterEditOpen(false);
    setRosterEditEntry(null);
    setRosterEditMain("");
    setRosterEditAlts("");
  };

  const handleRosterEditSave = async () => {
    if (!rosterEditEntry || !rosterEditMain.trim()) return;
    setRosterEditSaving(true);
    try {
      const ref = doc(db, "teams", effectiveTeamId, "Roster", rosterEditEntry.id);
      await updateDoc(ref, {
        mainCharacterName: rosterEditMain.trim(),
        altCharacters: rosterEditAlts
          .split(/\r?\n|,/)
          .map((s) => s.trim())
          .filter(Boolean)
      });

      const snap = await getDocs(collection(db, "teams", effectiveTeamId, "Roster"));
      setRoster(snap.docs.map((d) => ({ id: d.id, ...d.data() })));

      await bumpRosterVersion(effectiveTeamId);
      clearCachedRoster();
      setRosterEditOpen(false);
      setRosterEditEntry(null);
      setRosterEditMain("");
      setRosterEditAlts("");
    } finally {
      setRosterEditSaving(false);
    }
  };

  const TabButton = ({ label, tab }) =>
  <button
    onClick={() => setActiveTab(tab)}
    style={{
      padding: "8px 16px",
      borderRadius: "6px",
      border: "none",
      cursor: "pointer",
      fontWeight: "bold",
      backgroundColor: activeTab === tab ? "var(--theme-color)" : "#333",
      color: "#fff"
    }}>
    
      {label}
    </button>;



  const renderMembersTab = () =>
  <div className="custom-table-wrap">
    <div className="leader-member-table">
      <div className="leader-member-table__header">
        <div className="leader-member-table__cell">Username</div>
        <div className="leader-member-table__cell">Email</div>
        <div className="leader-member-table__cell">User Token</div>
        <div className="leader-member-table__cell">Verified</div>
        <div className="leader-member-table__cell leader-member-table__cell--actions">Actions</div>
      </div>
      {members.length === 0 ?
      <div className="leader-member-table__empty">No members found.</div> :
      members.map((member) => {
        const isVerified = Boolean(member?.verified);
        return (
          <div key={member.id} className="leader-member-table__row">
            <div className="leader-member-table__cell">
              <span className={member?.adminUser ? "admin-user-name" : undefined}>
                {member?.username || ""}
              </span>
            </div>
            <div className="leader-member-table__cell">{member?.email || ""}</div>
            <div className="leader-member-table__cell">
              <span
                className={`leader-member-table__token${member?.userToken ? " is-clickable" : ""}`}
                onClick={() => {
                  if (!member?.userToken) return;
                  navigator.clipboard
                    .writeText(member.userToken)
                    .then(() => alert("Copied token to clipboard!"))
                    .catch(() => alert("Failed to copy token."));
                }}>
                
                {member?.userToken || "None"}
              </span>
            </div>
            <div className="leader-member-table__cell">
              {isVerified ? "Verified" : "Pending"}
            </div>
            <div className="leader-member-table__cell leader-member-table__cell--actions">
              {isVerified ?
              <div className="leader-member-table__actions">
                <button
                  onClick={() => handleTokenGeneration(member.id, member.username)}
                  style={{ ...buttonStyle, width: "112px", height: "32px" }}>
                  
                  New Token
                </button>
                <button
                  onClick={() => {
                    if (window.confirm("Are you sure you want to remove this user from the team?")) {
                      handleRemove(member.id);
                    }
                  }}
                  style={{ ...dangerButtonStyle, width: "112px", height: "32px", marginLeft: 0 }}>
                  
                  Delete
                </button>
              </div> :
              <button
                onClick={() => handleApprove(member.id)}
                style={{ ...buttonStyle, width: "112px", height: "32px" }}>
                
                Approve
              </button>
              }
            </div>
          </div>);
      })
      }
    </div>
  </div>;


  const renderRosterTab = () =>
  <div className="custom-table-wrap">
    <div className="leader-roster-table">
      <div className="leader-roster-table__header">
        <div className="leader-roster-table__cell">Main Character</div>
        <div className="leader-roster-table__cell">Alt Characters</div>
        <div className="leader-roster-table__cell leader-roster-table__cell--actions">Actions</div>
      </div>
      {roster.length === 0 ?
      <div className="leader-roster-table__empty">No roster entries yet.</div> :
      roster.map((entry) => (
        <div key={entry.id} className="leader-roster-table__row">
          <div className="leader-roster-table__cell">{entry?.mainCharacterName || ""}</div>
          <div className="leader-roster-table__cell">
            {Array.isArray(entry?.altCharacters) ? entry.altCharacters.join(", ") : ""}
          </div>
          <div className="leader-roster-table__cell leader-roster-table__cell--actions">
            <div className="leader-roster-table__actions">
              <button
                onClick={() => openRosterEdit(entry)}
                style={{ ...buttonStyle, width: "102px" }}>
                
                Edit
              </button>
              <button
                onClick={() => {
                  if (window.confirm(`Delete "${entry.mainCharacterName}" from roster?`)) {
                    handleDeleteRosterEntry(entry.id);
                  }
                }}
                style={{ ...dangerButtonStyle, width: "102px", marginLeft: 0 }}>
                
                Delete
              </button>
            </div>
          </div>
        </div>
      ))
      }
    </div>

    <RosterForm />
  </div>;


  const renderEquipmentTab = () =>
  <div style={{ marginTop: "0.5rem", textAlign: "center" }}>
      <div
      style={{
        width: "100%",
        maxWidth: "720px",
        margin: "0 auto",
        padding: "0 1rem",
        boxSizing: "border-box"
      }}>
      
        <h3>Team Leader Name</h3>
        <div
        style={{
          margin: "1rem auto 1.5rem",
          padding: "1.25rem",
          borderRadius: "8px",
          border: "1px solid #ccc",
          opacity: 0.9,
          width: "100%",
          boxSizing: "border-box",
          textAlign: "left"
        }}>
        
          <label style={{ display: "block", fontWeight: "bold", marginBottom: "0.5rem" }}>
            Set team leader name
          </label>
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <input
            type="text"
            value={leaderNameInput}
            onChange={(e) => setLeaderNameInput(e.target.value)}
            placeholder="Enter leader display name"
            style={{
              flex: "1 1 260px",
              padding: "8px 10px",
              borderRadius: "6px",
              border: "1px solid #555",
              backgroundColor: "#1a1a1a",
              color: "#fff"
            }} />
          
            <button style={buttonStyle} onClick={handleLeaderNameSave}>
              Save Name
            </button>
          </div>
        </div>

        <h3>Delete Settings</h3>
        <div
        style={{
          margin: "1rem auto 0",
          padding: "1.25rem",
          borderRadius: "8px",
          border: "1px solid #ccc",
          opacity: 0.8,
          width: "100%",
          boxSizing: "border-box"
        }}>
        
          <div style={{ display: "grid", gap: "1rem" }}>
            <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1rem",
              flexWrap: "wrap"
            }}>
            
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: "bold" }}>Planet Scans</div>
                <div style={{ fontSize: "0.9rem", opacity: 0.9 }}>
                  {" "}
                  {scanCounts.galaxies ?? "N/A"} galaxies and {scanCounts.planets ?? "N/A"} planets
                </div>
              </div>
              <button
              style={dangerButtonStyle}
              onClick={openScanManage}>
              
                Delete Scans Menu
              </button>
            </div>

            <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1rem",
              flexWrap: "wrap"
            }}>
            
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: "bold" }}>DG Scans</div>
                <div style={{ fontSize: "0.9rem", opacity: 0.9 }}>
                  DGs Scanned: {dgScanCount ?? "N/A"}
                </div>
              </div>
              <button
              style={dangerButtonStyle}
              onClick={() =>
              openConfirm(
                "Delete all DG entries?",
                "This will delete all DG entries for your team.",
                () =>
                setTimeout(() =>
                openConfirm(
                  "FINAL WARNING",
                  <>
                        <div>This permanently deletes all DG entries for your team.</div>
                        <div
                      style={{
                        color: "#ff3b30",
                        fontWeight: "bold",
                        marginTop: "0.5rem",
                        textAlign: "center"
                      }}>
                      
                          THIS ACTION CANNOT BE UNDONE.
                        </div>
                      </>,
                  handleDeleteDgScanBatch
                ), 0)
              )
              }>
              
                Delete ALL DGs
              </button>
            </div>

            <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1rem",
              flexWrap: "wrap"
            }}>
            
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: "bold" }}>Proddy Entries</div>
                <div style={{ fontSize: "0.9rem", opacity: 0.9 }}>
                  Logged for your team: {proddyLogCount ?? "N/A"}
                </div>
            </div>
              <div style={{ display: "inline-flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button
                style={dangerButtonStyle}
                onClick={() =>
                openConfirm(
                  "Are you sure you want to delete all your prod data?",
                  <>
                        <div>This will remove ALL proddy log and snapshot entries for your team.</div>
                        <div
                      style={{
                        color: "#ff3b30",
                        fontWeight: "bold",
                        marginTop: "0.5rem",
                        textAlign: "center"
                      }}>
                      
                          THIS ACTION CANNOT BE UNDONE.
                        </div>
                      </>,
                  handleDeleteProddyTest
                )
                }>
                
                  Wipe Prod
                </button>
                <button
                style={buttonStyle}
                onClick={handleDownloadProddySnapshot}>
                
                  Save Snapshot
                </button>
              </div>
            </div>

            <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1rem",
              flexWrap: "wrap"
            }}>
             
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: "bold" }}>Custom Trade Bay</div>
                <div style={{ fontSize: "0.9rem", opacity: 0.9 }}>
                  Config status: {customTradeBayStatus}
                </div>
              </div>
              <button
              style={{
                ...dangerButtonStyle,
                opacity: canManageCustomTradeBay ? 1 : 0.6,
                cursor: canManageCustomTradeBay ? "pointer" : "not-allowed"
              }}
              onClick={openCustomTradeBay}
              disabled={!canManageCustomTradeBay}
              title={canManageCustomTradeBay ? "Edit custom trade bay" : "Leader/admin only"}>
               
                Trade Bay Settings - VERY WIP DONT USE
              </button>
            </div>
          </div>
        </div>
        {team &&
      <>
            <h3 style={{ marginTop: "1.5rem" }}>Theme Settings</h3>
          <div
          style={{
            margin: "1rem auto 0",
            padding: "1.25rem",
            borderRadius: "8px",
            border: "1px solid #ccc",
            width: "100%",
            boxSizing: "border-box"
          }}>
          
            <div style={{ display: "grid", gap: "1rem" }}>
              <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "1rem"
              }}>
              
                <span>Main</span>
                <input
                type="color"
                value={mainColor}
                onChange={(e) => setMainColor(e.target.value)}
                style={colorInputStyle} />
              
              </label>
              <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "1rem"
              }}>
              
                <span>Hover (Tables)</span>
                <input
                type="color"
                value={hoverColor}
                onChange={(e) => setHoverColor(e.target.value)}
                style={colorInputStyle} />
              
              </label>
              <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "1rem"
              }}>
              
                <span>Main Text</span>
                <input
                type="color"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
                style={colorInputStyle} />
              
              </label>
              <label
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "1rem"
              }}>
              
                <span>Boss Highlight</span>
                <input
                type="color"
                value={specialColor}
                onChange={(e) => setSpecialColor(e.target.value)}
                style={colorInputStyle} />
              
              </label>

              <button
              onClick={handleApplyColors}
              style={{
                backgroundColor: mainColor,
                color: textColor,
                padding: "10px",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "bold"
              }}>
              
                Apply Theme Colors
              </button>
            </div>
          </div>
          </>
      }
      </div>
    </div>;



  if (loading || loadingMembers || loadingRoster) return <p>Loading...</p>;

  if (loading || !userData) {
    return <p>Loading...</p>;
  }

  if (!isLeader) {
    return (
      <div style={{ textAlign: "center", color: "var(--theme-text-color)", padding: "2rem" }}>
        <h2>Access denied</h2>
        <p>This page is only available to team leaders.</p>
      </div>);

  }

  return (
    <div
      style={{
        border: "1px solid #444",
        borderRadius: "10px",
        padding: "1.5rem 1rem",
        backgroundColor: "#1e1e1e",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        width: "100%",
        maxWidth: "1320px",
        margin: "0 auto",
        boxSizing: "border-box"
      }}>
      
  {}
  <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "0.5rem",
          marginBottom: "1rem",
          flexWrap: "wrap"
        }}>
        
    <TabButton label="Registered Members" tab={TABS.MEMBERS} />
    <TabButton label="Roster Management" tab={TABS.ROSTER} />
    <TabButton label="Site Settings" tab={TABS.SITE_SETTINGS} />
  </div>

      {}
      <div
        style={{
          padding: "1.5rem 1rem",
          borderRadius: "6px",
          backgroundColor: "#2a2a2a",
          boxSizing: "border-box"
        }}>
        
    {activeTab === TABS.MEMBERS && renderMembersTab()}
    {activeTab === TABS.ROSTER && renderRosterTab()}
    {activeTab === TABS.SITE_SETTINGS && renderEquipmentTab()}
  </div>

    <Dialog
        open={confirmOpen}
        onClose={closeConfirm}
        aria-labelledby="leader-confirm-title"
        aria-describedby="leader-confirm-description"
        PaperProps={{
          className: "leader-confirm-paper"
        }}>
        
      <DialogTitle id="leader-confirm-title" sx={{ textAlign: "center" }}>
        {confirmTitle}
      </DialogTitle>
      <DialogContent sx={{ textAlign: "center" }}>
        <DialogContentText
            id="leader-confirm-description"
            className="leader-confirm-subtext">
            
          {confirmMessage}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
            onClick={closeConfirm}
            sx={{
              backgroundColor: "#4a4a4a",
              color: "#fff",
              "&:hover": { backgroundColor: "#5a5a5a" }
            }}>
            
          Cancel
        </Button>
        <Button
            onClick={handleConfirm}
            variant="contained"
            sx={{
              backgroundColor: "var(--theme-color)",
              color: "#fff",
              "&:hover": { backgroundColor: "var(--theme-color)" }
            }}>
            
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
        open={infoOpen}
        onClose={closeInfo}
        PaperProps={{ className: "leader-confirm-paper" }}>
        
      <DialogTitle>{infoTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText className="leader-confirm-subtext">
          {infoMessage}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
            onClick={closeInfo}
            sx={{
              backgroundColor: "#4a4a4a",
              color: "#fff",
              "&:hover": { backgroundColor: "#5a5a5a" }
            }}>
            
          Close
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
        open={rosterEditOpen}
        onClose={closeRosterEdit}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: "leader-confirm-paper" }}>
        
      <DialogTitle>Edit Roster Entry</DialogTitle>
      <DialogContent>
        <div
          style={{
            display: "grid",
            gap: "0.85rem",
            marginTop: "0.35rem"
          }}>
          
          <label style={{ display: "grid", gap: "0.35rem", textAlign: "left" }}>
            <span style={{ fontWeight: "bold" }}>Main Character</span>
            <input
              type="text"
              value={rosterEditMain}
              onChange={(e) => setRosterEditMain(e.target.value)}
              disabled={rosterEditSaving}
              style={{
                padding: "8px 10px",
                borderRadius: "6px",
                border: "1px solid #555",
                backgroundColor: "#1a1a1a",
                color: "#fff"
              }} />
            
          </label>
          <label style={{ display: "grid", gap: "0.35rem", textAlign: "left" }}>
            <span style={{ fontWeight: "bold" }}>Alt Characters</span>
            <textarea
              value={rosterEditAlts}
              onChange={(e) => setRosterEditAlts(e.target.value)}
              disabled={rosterEditSaving}
              rows={10}
              placeholder="One character per line, or comma separated"
              style={{
                padding: "8px 10px",
                borderRadius: "6px",
                border: "1px solid #555",
                backgroundColor: "#1a1a1a",
                color: "#fff",
                resize: "vertical",
                minHeight: "180px"
              }} />
            
          </label>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
            onClick={closeRosterEdit}
            disabled={rosterEditSaving}
            sx={{
              backgroundColor: "#4a4a4a",
              color: "#fff",
              "&:hover": { backgroundColor: "#5a5a5a" }
            }}>
            
          Cancel
        </Button>
        <Button
            onClick={handleRosterEditSave}
            disabled={rosterEditSaving || !rosterEditMain.trim()}
            variant="contained"
            sx={{
              backgroundColor: "var(--theme-color)",
              color: "#fff",
              "&:hover": { backgroundColor: "var(--theme-color)" }
            }}>
            
          {rosterEditSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
        open={scanManageOpen}
        onClose={closeScanManage}
        PaperProps={{ className: "leader-confirm-paper" }}>
        
      <DialogTitle>Team Scan Manager</DialogTitle>
      <DialogContent>
        <DialogContentText className="leader-confirm-subtext">
          {scanManageLoading ?
            "Loading..." :
            scanManageData ?
            "Delete things here." :
            "Failed to load scan list."}
        </DialogContentText>
        {!scanManageLoading && scanManageData &&
          <div style={{ marginTop: "0.75rem", display: "grid", gap: "1rem" }}>
            {Object.keys(scanManageData.galaxies || {}).length === 0 &&
            <div style={{ opacity: 0.8 }}>No scans found for your team.</div>
            }
            {Object.entries(scanManageData.galaxies || {}).map(([galaxy, bodies]) => {
              const planetList = (bodies || []).
              filter((body) => String(body.type).toLowerCase() === "planet").
              slice().
              sort((a, b) => a.name.localeCompare(b.name));
              const moonList = (bodies || []).
              filter((body) => String(body.type).toLowerCase() === "moon").
              slice().
              sort((a, b) => a.name.localeCompare(b.name));

              return (
                <div
                  key={galaxy}
                  style={{
                    border: "1px solid #444",
                    borderRadius: "8px",
                    padding: "0.75rem"
                  }}>
                  
                <div
                    style={{
                      textAlign: "center",
                      fontWeight: "bold",
                      marginBottom: "0.75rem"
                    }}>
                    
                  {galaxy}
                </div>
                <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                      gap: "1rem"
                    }}>
                    
                  <div>
                    <div
                        style={{
                          fontWeight: "bold",
                          paddingBottom: "0.35rem",
                          borderBottom: "1px solid #333"
                        }}>
                        
                      Planets
                    </div>
                    <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                          gap: "0.5rem",
                          marginTop: "0.5rem"
                        }}>
                        
                      {planetList.length === 0 && <div style={{ opacity: 0.7 }}>None</div>}
                      {planetList.map((planet) =>
                        <div
                          key={planet.id}
                          style={{
                            border: "1px solid #333",
                            borderRadius: "6px",
                            padding: "0.5rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.5rem"
                          }}>
                          
                          <div
                            style={{
                              fontWeight: "bold",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}>
                            
                            {planet.name}
                          </div>
                          <button
                            style={dangerButtonStyle}
                            onClick={() =>
                            openConfirm(
                              `Delete ${planet.name}?`,
                              "This will remove this scan entry for your team.",
                              () => handleDeleteBodyScan(planet.id, planet.name)
                            )
                            }>
                            
                            Delete
                          </button>
                        </div>
                        )}
                    </div>
                  </div>
                  <div>
                    <div
                        style={{
                          fontWeight: "bold",
                          paddingBottom: "0.35rem",
                          borderBottom: "1px solid #333"
                        }}>
                        
                      Moons
                    </div>
                    <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                          gap: "0.5rem",
                          marginTop: "0.5rem"
                        }}>
                        
                      {moonList.length === 0 && <div style={{ opacity: 0.7 }}>None</div>}
                      {moonList.map((moon) =>
                        <div
                          key={moon.id}
                          style={{
                            border: "1px solid #333",
                            borderRadius: "6px",
                            padding: "0.5rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.5rem"
                          }}>
                          
                          <div
                            style={{
                              fontWeight: "bold",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}>
                            
                            {moon.name}
                          </div>
                          <button
                            style={dangerButtonStyle}
                            onClick={() =>
                            openConfirm(
                              `Delete ${moon.name}?`,
                              "This will remove this scan entry for your team.",
                              () => handleDeleteBodyScan(moon.id, moon.name)
                            )
                            }>
                            
                            Delete
                          </button>
                        </div>
                        )}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: "0.75rem", textAlign: "center" }}>
                  <button
                      style={dangerButtonStyle}
                      onClick={() =>
                      openConfirm(
                        `Delete all scans in ${galaxy}?`,
                        "This will remove every planet and moon scan your team uploaded for this galaxy.",
                        () => handleDeleteGalaxyScans(galaxy)
                      )
                      }>
                      
                    Delete {galaxy}
                  </button>
                </div>
              </div>);

            })}
          </div>
          }
      </DialogContent>
      <DialogActions>
        <Button
            onClick={closeScanManage}
            sx={{
              backgroundColor: "#4a4a4a",
              color: "#fff",
              "&:hover": { backgroundColor: "#5a5a5a" }
            }}>
            
          Close
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
        open={customTradeBayOpen}
        onClose={closeCustomTradeBay}
        fullWidth
        maxWidth="md"
        PaperProps={{ className: "leader-confirm-paper" }}>
      
      <DialogTitle>Custom Trade Bay</DialogTitle>
      <DialogContent>
        {customTradeBayLoading ?
        <DialogContentText className="leader-confirm-subtext">
            Loading custom trade bay settings...
          </DialogContentText> :
        <>
            <div
            style={{
              display: "grid",
              gap: "0.5rem",
              maxHeight: "320px",
              overflowY: "auto",
              marginTop: "0.5rem"
            }}>
              {customTradeBayItems.length === 0 &&
            <div style={{ opacity: 0.8 }}>No items yet. Add an item below.</div>
            }
              {customTradeBayItems.map((item, index) =>
            <div
                key={`${item.name}-${index}`}
                style={{
                  border: "1px solid #444",
                  borderRadius: "6px",
                  padding: "0.5rem 0.75rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  justifyContent: "space-between"
                }}>
                
                  <div style={{ flex: "1 1 auto", textAlign: "left" }}>{item.name}</div>
                  <input
                  type="number"
                  min={1}
                  step={1}
                  value={item.qty}
                  onChange={(e) => handleCustomTradeBayQtyChange(index, e.target.value)}
                  style={{
                    width: "96px",
                    padding: "6px 8px",
                    borderRadius: "6px",
                    border: "1px solid #666",
                    backgroundColor: "#1a1a1a",
                    color: "#fff"
                  }} />
                
                  <button
                  onClick={() => handleCustomTradeBayRemove(index)}
                  style={{
                    border: "none",
                    borderRadius: "4px",
                    width: "32px",
                    height: "32px",
                    cursor: "pointer",
                    fontWeight: "bold",
                    color: "#fff",
                    backgroundColor: "#b3261e"
                  }}
                  title="Remove item">
                  
                    X
                  </button>
                </div>
            )}
            </div>
            <div
            style={{
              marginTop: "1rem",
              borderTop: "1px solid #333",
              paddingTop: "0.75rem",
              display: "grid",
              gap: "0.5rem"
            }}>
              <div style={{ fontWeight: "bold", textAlign: "left" }}>Add item</div>
              <input
              type="text"
              list="custom-tradebay-suggestions"
              value={customTradeBaySuggestionInput}
              onChange={(e) => setCustomTradeBaySuggestionInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCustomTradeBayAdd();
                }
              }}
              placeholder="Search from defaults/current list"
              style={{
                padding: "8px 10px",
                borderRadius: "6px",
                border: "1px solid #555",
                backgroundColor: "#1a1a1a",
                color: "#fff"
              }} />
            
              <datalist id="custom-tradebay-suggestions">
                {customTradeBaySuggestions.map((itemName) =>
              <option key={itemName} value={itemName} />
              )}
              </datalist>
              <input
              type="text"
              value={customTradeBayNewItemInput}
              onChange={(e) => setCustomTradeBayNewItemInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCustomTradeBayAdd();
                }
              }}
              placeholder="Or enter a new item name"
              style={{
                padding: "8px 10px",
                borderRadius: "6px",
                border: "1px solid #555",
                backgroundColor: "#1a1a1a",
                color: "#fff"
              }} />
            
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                onClick={handleCustomTradeBayAdd}
                style={buttonStyle}>
                
                  Add
                </button>
              </div>
            </div>
          </>
        }
      </DialogContent>
      <DialogActions>
        <Button
            onClick={closeCustomTradeBay}
            sx={{
              backgroundColor: "#4a4a4a",
              color: "#fff",
              "&:hover": { backgroundColor: "#5a5a5a" }
            }}>
          
          Cancel
        </Button>
        <Button
            onClick={handleCustomTradeBaySave}
            disabled={customTradeBayLoading || customTradeBaySaving || !canManageCustomTradeBay}
            variant="contained"
            sx={{
              backgroundColor: "var(--theme-color)",
              color: "#fff",
              "&:hover": { backgroundColor: "var(--theme-color)" }
            }}>
          
          {customTradeBaySaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
    </div>);

};

export default TeamLeaderPanel;
